<template>
  <div>
    <div v-if="resume"
         class="bg-white shadow overflow-hidden sm:rounded-lg">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          {{$t('resume.details.title')}}
        </h3>
        <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
          {{$t('resume.details.description')}}
        </p>
      </div>
      <div>
        <dl>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('generic.phone')}}
            </dt>
            <dd class="list-row-dd">
              {{resume.phone_number || $t('generic.na')}}
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('resume.position')}}
            </dt>
            <dd class="list-row-dd">
              {{resume.position || $t('generic.na')}}
            </dd>
          </div>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('resume.info')}}
            </dt>
            <dd class="list-row-dd">
              {{resume.info || $t('generic.na')}}
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('generic.address')}}
            </dt>
            <dd class="list-row-dd">
              {{fullAddress}}
            </dd>
          </div>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('resume.salary')}}
            </dt>
            <dd class="list-row-dd">
              <template v-if="resume.salary_min">
                {{resume.salary_min | toCurrency}}
              </template>
              <template v-if="resume.salary_type">
                / {{resume.salary_type}}
              </template>
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('generic.status')}}
            </dt>
            <dd class="list-row-dd">
              <status-badge :status="resume.status"/>
            </dd>
          </div>
          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('resume.open_to_relocate')}}
            </dt>
            <dd class="list-row-dd">
              <status-badge :status="resume.open_to_relocate ? 'yes': 'no'"/>
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('resume.cv')}}
            </dt>
            <dd class="list-row-dd">
              <div class="flex-shrink-0">
                <a href="#"
                   @click.prevent="downloadFile"
                   class="font-medium text-primary hover:text-underline transition duration-150 ease-in-out">
                  <i v-if="loading" class="el-icon-loading"></i>
                  {{$t('generic.download')}}
                </a>
              </div>
            </dd>
          </div>
          <div
            class="bg-gray-50 mt-8 sm:mt-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-t sm:border-gray-200 sm:px-6 sm:py-5">
            <dt class="list-row-dt">
              {{$t('resume.education')}}
            </dt>
            <dd v-loading="resumeHistoryLoading"
                class="list-row-dd">
              <ul v-if="educationHistory.length" class="border border-gray-200 rounded-md">
                <li v-for="(education, index) in educationHistory"
                    :key="education.name"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5"
                    :class="{'border-t border-gray-200': index >  0}"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center">
                      <span class="truncate font-semibold text-primary">
                        {{education.name}}
                      </span>
                      <span class="ml-2 truncate">
                        {{$t('generic.at')}} {{education.institution}}
                      </span>
                    </div>
                    <div class="text-sm leading-5 text-gray-900 my-1 flex items-center">
                      <calendar-icon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-700"/>
                      <template v-if="education.from">
                        <time :datetime="education.from">{{$formatDate(education.from, dateFormat)}}
                        </time>
                      </template>
                      <template v-if="education.till">
                        &nbsp; - &nbsp;
                        <time :datetime="education.till">{{$formatDate(education.till, dateFormat)}}
                        </time>
                      </template>
                    </div>
                    <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                      {{education.description}}
                    </p>
                  </div>
                  <span
                    class="ml-auto inline-block py-1 px-3 text-xs leading-4 rounded-full bg-gray-100 group-hover:bg-gray-200 group-focus:bg-gray-200">
                      {{education.place}}
                    </span>
                </li>
              </ul>
              <span v-if="!resumeHistoryLoading && educationHistory.length === 0">
                  {{$t('generic.noData')}}
              </span>
            </dd>
          </div>

          <div
            class="bg-white mt-8 sm:mt-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-t sm:border-gray-200 sm:px-6 sm:py-5">
            <dt class="list-row-dt">
              {{$t('resume.employerHistory')}}
            </dt>
            <dd class="list-row-dd" v-loading="resumeHistoryLoading">
              <ul v-if="employerHistory.length"
                  class="border border-gray-200 rounded-md">
                <li v-for="(history, index) in employerHistory"
                    :key="history.name"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5"
                    :class="{'border-t border-gray-200': index >  0}"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center">
                      <span class="truncate font-semibold text-primary">
                        {{history.name}}
                      </span>
                      <span class="ml-2 truncate">
                        {{$t('generic.at')}} {{history.institution}}
                      </span>
                    </div>
                    <div class="text-sm leading-5 text-gray-900 my-1 flex items-center">
                      <calendar-icon class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-700"/>
                      <template v-if="history.from">
                        <time :datetime="history.from">{{$formatDate(history.from, dateFormat)}}
                        </time>
                      </template>
                      <template v-if="history.till">
                        &nbsp; - &nbsp;
                        <time :datetime="history.till">{{$formatDate(history.till, dateFormat)}}
                        </time>
                      </template>
                    </div>
                    <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                      {{history.description}}
                    </p>
                  </div>
                  <span
                    class="ml-auto inline-block py-1 px-3 text-xs leading-4 rounded-full bg-gray-100 group-hover:bg-gray-200 group-focus:bg-gray-200">
                      {{history.place}}
                    </span>
                </li>
              </ul>
              <span v-if="!resumeHistoryLoading && employerHistory.length === 0">
                  {{$t('generic.noData')}}
              </span>
            </dd>
          </div>

          <div class="bg-gray-50 list-row">
            <dt class="list-row-dt">
              {{$t('resume.travelDistance')}}
            </dt>
            <dd class="list-row-dd">
              <template v-if="resume.travel_distance">
                {{resume.travel_distance}} {{distanceSymbol}}
              </template>
              <template v-else>
                {{$t('generic.na')}}
              </template>
            </dd>
          </div>
          <div class="bg-white list-row">
            <dt class="list-row-dt">
              {{$t('generic.createdAt')}}
            </dt>
            <dd class="list-row-dd">
              {{$formatDate(resume.created_at, FRIENDLY_DATETIME_FORMAT)}}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>
<script>
  import get from 'lodash/get'
  import FileService from 'src/services/FileService'
  import { CalendarIcon } from 'vue-feather-icons'
  import { FRIENDLY_DATE_FORMAT, FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'
  import ResumeService from 'src/services/ResumeService'

  export default {
    components: {
      CalendarIcon,
    },
    data () {
      return {
        loading: false,
        dateFormat: FRIENDLY_DATE_FORMAT,
        FRIENDLY_DATETIME_FORMAT,
        FRIENDLY_DATE_FORMAT,
        resumeHistoryLoading: false,
        resumeHistory: [],
      }
    },
    computed: {
      resume () {
        return this.$store.state.resume.currentResume || {}
      },
      fullAddress () {
        const {address, city, country, postal_code} = this.resume
        let fullAddress = ''
        fullAddress = this.appendToString(address)
        fullAddress = this.appendToString(postal_code)
        fullAddress = this.appendToString(city)
        fullAddress = this.appendToString(country)

        return fullAddress
      },
      distanceSymbol () {
        return this.$store.getters['config/distanceSymbol']
      },
      employerHistory () {
        return this.resumeHistory.filter(h => h.type === 'employer')
      },
      educationHistory () {
        return this.resumeHistory.filter(h => h.type === 'education')
      },
    },
    created () {
      this.getHistory()
    },
    methods: {
      get,
      appendToString (str, toAppend) {
        if (toAppend && !str) {
          str += toAppend
          return str
        }
        if (toAppend) {
          str += `, ${str}`
        }
        return str
      },
      async downloadFile () {
        const resume = this.resume
        try {
          const date = this.$formatDate(resume.created_at, FRIENDLY_DATETIME_FORMAT)
          let name = `CV_${date}`
          this.loading = true
          await FileService.downloadFile(resume.file_token, name)
        } catch (e) {
          this.$notify({
            type: 'danger',
            message: this.$t('resume.download.error')
          })
        } finally {
          this.loading = false
        }
      },
      async getHistory () {
        try {
          this.resumeHistoryLoading = true
          this.resumeHistory = await ResumeService.getResumeHistory(this.$route.params.id)
        } finally {
          this.resumeHistoryLoading = false
        }
      }
    }
  }
</script>
